import React, { useState } from 'react';
import { recordGeneratedReport } from '../utils/recordGeneratedReport';
import { functions } from '../../firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import AnalysisReportShell from './reports/AnalysisReportShell';
import type { GlobalThreatGroupAnalysisResult, Organization, Page, NavParams } from '@types';
import { COUNTRIES } from '../../services/locationData';
import Loader from './Loader';
import ThreatGroupBriefing from './ThreatGroupBriefing';
import ApiErrorDisplay from './ApiErrorDisplay';

interface GlobalThreatGroupDashboardProps {
    onBack?: () => void;
    onNavigate?: (nav: { page: Page; params?: NavParams }) => void;
    organization?: Organization | null;
    params?: NavParams;
}

// --- REFACTORED GlobalThreatGroupDashboard ---
const GlobalThreatGroupDashboard: React.FC<GlobalThreatGroupDashboardProps> = ({ onBack, onNavigate, organization, params }) => {
    // --- STATE MANAGEMENT ---
    const [selectedCountry, setSelectedCountry] = useState<string>(params?.preselectedCountry || '');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [foundGroups, setFoundGroups] = useState<string[] | null>(null);
    const [analysisResult, setAnalysisResult] = useState<GlobalThreatGroupAnalysisResult | null>(null);
    
    const [manualGroupName, setManualGroupName] = useState<string>('');
    const [isLoadingAccordionGroup, setIsLoadingAccordionGroup] = useState<string | null>(null);
    
    // Ref to track the current active request ID to prevent race conditions
    const activeRequestIdRef = React.useRef<number>(0);

    // Contextual State
    const projectContext = params?.projectContext || params?.srmProject;
    const nearbyAssets = params?.assets;

    React.useEffect(() => () => { activeRequestIdRef.current++; }, []);

    // Auto-search if country provided
    React.useEffect(() => {
        if (params?.preselectedCountry) {
            setSelectedCountry(params.preselectedCountry);
            void handleFindGroupsByCountry(undefined, params.preselectedCountry);
        }
    }, [params?.preselectedCountry]);

    // --- CORE ANALYSIS HANDLER (NEW) ---
    const handleAnalysisRequest = async (groupName: string) => {
        if (!groupName.trim())
            return;
        const requestId = ++activeRequestIdRef.current;
        setIsLoading(true);
        setIsLoadingAccordionGroup(groupName);
        setAnalysisResult(null);
        setError(null);

        try {
            // 1. Live API call with context
            console.log(`Performing live analysis for ${groupName} with location context.`);
            const analyzeFunc = httpsCallable<{
                groupName: string, 
                country?: string,
                locationContext?: any, 
                nearbyAssets?: any[]
            }, {result: GlobalThreatGroupAnalysisResult}>(functions, 'performThreatGroupAnalysis', { timeout: 540000 });
            
            const locationContext = projectContext ? {
                name: projectContext.name,
                lat: projectContext.lat,
                lng: projectContext.lng,
                address: projectContext.locationAddress
            } : undefined;

            const response = await analyzeFunc({ 
                groupName, 
                country: foundGroups?.includes(groupName) ? selectedCountry : undefined,
                locationContext,
                nearbyAssets
            });
            
            if (requestId !== activeRequestIdRef.current)
                return;
            const liveAnalysis = { ...response.data.result, organizationId: organization?.id || projectContext?.organizationId,
                clientId: params?.clientId || projectContext?.clientId, assetId: projectContext?.assetId,
                srmProjectId: projectContext?.id, srmModuleId: params?.srmModule?.id,
                workflowId: 'threat_group_network_analysis' };
            setIsLoading(false);
            setIsLoadingAccordionGroup(null);
            setAnalysisResult(liveAnalysis);
            await recordGeneratedReport(liveAnalysis.organizationId, liveAnalysis, `Threat Group Intelligence: ${groupName}`, 'osint', liveAnalysis.clientId);

        } catch (e: any) {
            console.error("ANALYSIS ERROR IN DASHBOARD:", e);
            if (requestId === activeRequestIdRef.current)
                setError(e.message || `An error occurred while analyzing ${groupName}.`);
        } finally {
            if (requestId === activeRequestIdRef.current) {
                setIsLoadingAccordionGroup(null);
                setIsLoading(false);
            }
        }
    };

    // --- UI EVENT HANDLERS ---
    const handleFindGroupsByCountry = async (event?: React.FormEvent, countryOverride?: string) => {
        if (event) event.preventDefault();
        const searchCountry = countryOverride ?? selectedCountry;
        const requestId = ++activeRequestIdRef.current;
        setIsLoadingAccordionGroup(null);
        if (!searchCountry) {
            setFoundGroups(null);
            setAnalysisResult(null);
            setError(null);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError(null);
        setFoundGroups(null);
        setAnalysisResult(null);

        try {
            const searchFunc = httpsCallable<{country: string, type?: string}, {result: string[]}>(functions, 'searchThreatGroupsByCountry', { timeout: 300000 });
            const response = await searchFunc({ country: searchCountry });
            if (requestId !== activeRequestIdRef.current)
                return;
            const groupNames = [...new Set(response.data.result.filter(name => typeof name === 'string' && name.trim()))];
            
            setFoundGroups(groupNames);
            if (groupNames.length === 0) {
                setError(`No significant threat groups identified for ${searchCountry}.`);
            }
        } catch (e: any) {
            if (requestId === activeRequestIdRef.current)
                setError(e.message || 'An error occurred while searching.');
        } finally {
            if (requestId === activeRequestIdRef.current)
                setIsLoading(false);
        }
    };

    const handleDirectOrManualRequest = (groupName: string) => {
        void handleAnalysisRequest(groupName);
    };

    const handleBack = () => {
        if (onBack) onBack();
        else if (onNavigate) onNavigate({ page: 'dashboard' });
    };

    if (analysisResult) {
        return (
            <div className="animate-fade-in space-y-4 h-full flex flex-col">
                <button onClick={() => { activeRequestIdRef.current++; setAnalysisResult(null); setIsLoading(false); setIsLoadingAccordionGroup(null); setError(null); }} className="flex items-center text-sm font-medium text-slate-600 hover:text-blue-600 w-max">
                    &larr; Back to Threat Search
                </button>
                <div className="flex-grow bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden relative">
                    <AnalysisReportShell title="Threat Group Intelligence" subtitle={analysisResult.groupName || 'Threat group analysis'}
                        report={analysisResult} organizationId={organization?.id} clientId={params?.clientId || projectContext?.clientId}
                        srmProject={projectContext} srmModule={params?.srmModule} preferredWorkflowId="threat_group_network_analysis">
                        <ThreatGroupBriefing
                            key={analysisResult.groupName || 'current-analysis'}
                            result={analysisResult}
                            onNavigate={onNavigate}
                        />
                    </AnalysisReportShell>
                </div>
            </div>
        );
    }

    return (
        <div className="animate-fade-in space-y-6">
            <button onClick={handleBack} className="flex items-center text-sm font-medium text-slate-600 hover:text-blue-600">
                &larr; Back to Dashboard
            </button>

            <h2 className="text-2xl font-bold text-slate-800">Threat Group OSINT Dashboard</h2>
            <p className="text-slate-500 -mt-4">Investigate state-sponsored and non-state threat actors.</p>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* --- INPUT FORMS --- */}
                <div className="lg:col-span-1 space-y-4">
                    {/* Find by Country */}
                    <form onSubmit={handleFindGroupsByCountry} className="bg-white p-4 rounded-lg border shadow-sm">
                        <h3 className="font-semibold mb-2 text-slate-700">Find Groups by Country</h3>
                        <select value={selectedCountry} aria-label="Country" onChange={(e) => { setSelectedCountry(e.target.value); void handleFindGroupsByCountry(undefined, e.target.value); }} className="w-full p-2 border rounded-md bg-slate-50">
                            <option value="">Select...</option>
                            {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <button type="submit" disabled={isLoading || !selectedCountry} className="w-full mt-2 bg-blue-600 text-white p-2 rounded-md hover:bg-blue-700 disabled:bg-slate-400">
                            Find Groups
                        </button>
                    </form>
                    {/* Analyze Known Actors */}
                    <div className="bg-white p-4 rounded-lg border shadow-sm">
                        <h3 className="font-semibold mb-2 text-slate-700">Analyze Known Actors</h3>
                        <select aria-label="Known actors in selected country" value="" disabled={isLoading || !foundGroups?.length} onChange={(e) => handleDirectOrManualRequest(e.target.value)} className="w-full p-2 border rounded-md bg-slate-50 disabled:text-slate-400">
                            <option value="">{!selectedCountry ? 'Select a country first...' : isLoading ? 'Loading...' : 'Select actor...'}</option>
                            {(foundGroups || []).map(actor => <option key={actor} value={actor}>{actor}</option>)}
                        </select>
                        <p className="mt-2 text-xs text-slate-500">Actors identified for the selected country.</p>
                    </div>
                    {/* Search by Name */}
                    <form onSubmit={(e) => { e.preventDefault(); handleDirectOrManualRequest(manualGroupName); }} className="bg-white p-4 rounded-lg border shadow-sm">
                        <h3 className="font-semibold mb-2 text-slate-700">Search by Name</h3>
                        <input type="text" value={manualGroupName} onChange={(e) => setManualGroupName(e.target.value)} placeholder="e.g., Al-Shabaab" className="w-full p-2 border rounded-md bg-slate-50" />
                        <button type="submit" disabled={isLoading || !manualGroupName.trim()} className="w-full mt-2 bg-blue-600 text-white p-2 rounded-md hover:bg-blue-700 disabled:bg-slate-400">
                            Search
                        </button>
                    </form>
                </div>

                {/* --- DISPLAY AREA --- */}
                <div className="lg:col-span-2">
                    {isLoading && <Loader />}
                    <ApiErrorDisplay error={error} serviceName="Threat Group Analysis" />

                    {/* Display found groups from country search */}
                    {foundGroups && (
                        <div className="space-y-2">
                            <h3 className="font-semibold text-slate-700 mb-2">{foundGroups.length} groups found for {selectedCountry}:</h3>
                            {foundGroups.map(groupName => (
                                <div key={groupName} className="border rounded-lg overflow-hidden bg-white">
                                    <button
                                        disabled={isLoading}
                                        onClick={() => handleAnalysisRequest(groupName)}
                                        className="w-full text-left p-3 font-semibold bg-slate-50 hover:bg-slate-100 flex justify-between items-center"
                                    >
                                        <span>{groupName}</span>
                                        {isLoadingAccordionGroup === groupName 
                                            ? <div className="h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                            : <span className="text-blue-600 text-sm">Analyze &rarr;</span>
                                        }
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                    
                </div>
            </div>
        </div>
    );
};

export default GlobalThreatGroupDashboard;
